import React from 'react';
import { Building2, CheckCircle, Shield, Users, FileText } from 'lucide-react';

export const CompanyFormation = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-4 py-2 rounded-full mb-6">
              <Building2 className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-semibold">Business Formation Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Professional Company <span className="text-yellow-400">Formation</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Start your business the right way. We handle all the paperwork, so you can focus on building your empire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Form Your Company
              </button>
              <button
                onClick={onOpenPopup}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Launch Your Business with <span className="text-red-600">Confidence</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Starting a business is exciting, but the legal requirements can be overwhelming. Choosing the right business structure affects your taxes, liability protection, and ability to raise capital. Make the wrong choice, and you could pay for it for years.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our company formation experts guide you through every decision, from selecting the optimal entity type to filing all required paperwork. Whether you're forming an LLC for liability protection, incorporating for investor funding, or establishing a partnership, we ensure your business starts on solid legal ground.
              </p>
              <div className="space-y-3">
                {[
                  'Personal asset protection from business debts',
                  'Tax optimization and deduction opportunities',
                  'Professional business credibility',
                  'Easier access to business financing',
                  'Formal ownership structure',
                  'Compliance with state regulations'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop"
                alt="Company formation"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Entity Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose the Right <span className="text-red-600">Business Structure</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each business structure offers different advantages. We'll help you choose the one that best fits your goals.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'LLC',
                fullName: 'Limited Liability Company',
                description: 'Perfect for small to medium businesses seeking liability protection with simple management',
                benefits: [
                  'Personal asset protection',
                  'Pass-through taxation',
                  'Flexible management',
                  'Less paperwork than corporations'
                ],
                bestFor: 'Most small businesses'
              },
              {
                name: 'C-Corp',
                fullName: 'C Corporation',
                description: 'Ideal for businesses seeking venture capital or planning significant growth and expansion',
                benefits: [
                  'Unlimited growth potential',
                  'Easy to raise capital',
                  'Multiple stock classes',
                  'Strong liability protection'
                ],
                bestFor: 'High-growth startups'
              },
              {
                name: 'S-Corp',
                fullName: 'S Corporation',
                description: 'Combines corporate protection with pass-through taxation for qualifying small businesses',
                benefits: [
                  'Tax advantages',
                  'Liability protection',
                  'Self-employment tax savings',
                  'Corporate credibility'
                ],
                bestFor: 'Profitable small businesses'
              },
              {
                name: 'Partnership',
                fullName: 'General/Limited Partnership',
                description: 'Suitable for businesses with multiple owners who want to share management and profits',
                benefits: [
                  'Simple to establish',
                  'Shared responsibilities',
                  'Pass-through taxation',
                  'Flexible profit distribution'
                ],
                bestFor: 'Multi-owner businesses'
              },
              {
                name: 'Nonprofit',
                fullName: 'Nonprofit Corporation',
                description: 'For organizations focused on charitable, educational, or social causes',
                benefits: [
                  'Tax-exempt status',
                  'Grant eligibility',
                  'Liability protection',
                  'Public trust'
                ],
                bestFor: 'Charitable organizations'
              },
              {
                name: 'DBA',
                fullName: 'Doing Business As',
                description: 'Allows you to operate under a different name while maintaining your existing entity',
                benefits: [
                  'Brand flexibility',
                  'Multiple business names',
                  'Quick setup',
                  'Low cost'
                ],
                bestFor: 'Name registration only'
              }
            ].map((entity, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="bg-red-100 px-4 py-2 rounded-full inline-block mb-4">
                  <span className="text-red-600 font-bold text-lg">{entity.name}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{entity.fullName}</h3>
                <p className="text-gray-600 mb-4">{entity.description}</p>
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Key Benefits:</div>
                  <ul className="space-y-2">
                    {entity.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-yellow-50 px-4 py-2 rounded-lg">
                  <div className="text-xs font-semibold text-yellow-700">Best For:</div>
                  <div className="text-sm text-gray-700">{entity.bestFor}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Included */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What's <span className="text-red-600">Included?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: FileText,
                title: 'Entity Selection',
                description: 'Expert guidance on choosing the right structure for your business goals'
              },
              {
                icon: Shield,
                title: 'State Filing',
                description: 'Complete preparation and filing of all formation documents'
              },
              {
                icon: Users,
                title: 'Operating Documents',
                description: 'Customized operating agreements or bylaws for your business'
              },
              {
                icon: Building2,
                title: 'Registered Agent',
                description: 'First year of registered agent service included in most states'
              }
            ].map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Simple Formation <span className="text-red-600">Process</span>
            </h2>
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'Free Consultation',
                  description: 'Discuss your business goals and receive personalized recommendations on the best entity structure for your needs.'
                },
                {
                  step: '02',
                  title: 'Document Preparation',
                  description: 'We prepare all required formation documents including articles of organization/incorporation and operating agreements.'
                },
                {
                  step: '03',
                  title: 'State Filing',
                  description: 'Your formation documents are filed with the appropriate state agency. Most filings are completed within 5-10 business days.'
                },
                {
                  step: '04',
                  title: 'Receive Your Documents',
                  description: 'Once approved, you receive your official formation documents, EIN, and everything needed to open bank accounts and conduct business.'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-6 bg-white p-6 rounded-xl shadow-lg">
                  <div className="bg-red-600 text-white font-bold text-2xl w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Form your company today and get expert guidance every step of the way.
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300"
          >
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
};